import { useState, useCallback } from 'react'
import type { Patient } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import { useAuth } from '../../hooks/use-auth'
import {
  PageHeader,
  DataTable,
  Pagination,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'
import { PatientEditModal } from './PatientEditModal'
import { BulkImportModal } from '../../components/BulkImportModal'
import { getAuthToken } from '../../services/api'

export function PatientListPage() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<Patient>({
    path: '/patients',
    filters: { search: debouncedSearch || undefined },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('patients')

  function openEditModal(patient: Patient) {
    setSelectedId(patient.id)
    setIsNew(false)
    setModalOpen(true)
  }

  function openNewModal() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  const handleExportCsv = useCallback(() => {
    const params = new URLSearchParams({ format: 'csv' })
    if (debouncedSearch) params.set('search', debouncedSearch)
    const url = `/api/bulk/patient/export?${params.toString()}`
    const token = getAuthToken()
    void fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (res) => {
      if (!res.ok) return
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const filename = match ? match[1] : `patient-${new Date().toISOString().slice(0, 10)}.csv`
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      link.click()
      URL.revokeObjectURL(objectUrl)
    })
  }, [debouncedSearch])

  const columns: readonly Column<Patient>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      filterable: true,
      hideable: false,
      render: (p) => (
        <strong>
          {p.lastName}, {p.firstName}
        </strong>
      ),
      sortValue: (p) => `${p.lastName}, ${p.firstName}`,
    },
    { key: 'mrn', header: 'MRN', sortable: true, filterable: true, render: (p) => p.medicalRecordNumber || '--' },
    { key: 'dob', header: 'DOB', sortable: true, render: (p) => p.dateOfBirth },
    { key: 'sex', header: 'Sex', sortable: true, render: (p) => p.sex },
    { key: 'phone', header: 'Phone', render: (p) => p.phone || '--' },
  ]

  return (
    <div>
      <PageHeader
        title="Patients"
        subtitle={`${total} total`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              Add Patient
            </button>
            {user?.role !== 'tech' && (
              <button type="button" onClick={() => setImportModalOpen(true)} style={btnSecondary}>
                Import CSV
              </button>
            )}
            <button type="button" onClick={handleExportCsv} style={btnSecondary}>
              Export CSV
            </button>
          </div>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '300px' }}
          placeholder="Search by name or MRN..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEditModal}
            emptyMessage="No patients found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <PatientEditModal
        patientId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onArchived={() => refresh()}
      />

      <BulkImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="patient"
        entityLabel="Patients"
        onSuccess={refresh}
      />
    </div>
  )
}
